import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import useGetLastProcessDate from "../api/useGetLastProcessDate";
import buildAndCleanQueryString from "../utils/buildAndCleanQueryString";
import { formatUTCDate } from "../utils/dateFormatter";

/**
 * This hook is used to handle getting and setting Process Date.
 * Both in the URL and in the state.
 * It will default to the last process date from the lastProcessDate API.
 * This hook also provides a param for what state is showing compared to what is selected.
 * Because there is a case where the process date could be cleared
 * by the user and is still showing data.
 *
 * should take and return date string format of "MM/DD/YYYY"
 * @returns [date string, date string, (date: date string | null) => void]
 */

export default function useProcessDateFormatted(
  callback: (date: string) => void
): [string, string, (date: string | null) => void] {
  const [InputValue, setInputValue] = useState<string>("");
  const [DisplayValue, setDisplayValue] = useState<string>("");

  const [searchParams, setSearchParams] = useSearchParams(""); // managing URL

  const updateUrl = useCallback((date: string | null) => {
    const queryString = buildAndCleanQueryString(
      { processDate: date },
      {
        replaceSlashes: ["processDate"],
      }
    );
    setSearchParams(queryString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateProcessDate = useCallback(
    (searchParam: string | null, processDate?: string) => {
      if (searchParam) return searchParam.replaceAll("-", "/");
      if (processDate) {
        return formatUTCDate(processDate);
      }
      return "";
    },
    []
  );

  // last process date from API
  const { data: lastProcessDateFromAPI } = useGetLastProcessDate();

  // set process date when lastProcessDateFromAPI returns
  useEffect(() => {
    const updatedDate = updateProcessDate(
      searchParams.get("processDate"),
      lastProcessDateFromAPI
    );
    setInputValue(updatedDate);
    setDisplayValue(updatedDate);

    // set url on first load
    if (!searchParams.has("processDate")) {
      updateUrl(updatedDate);
    } else {
      // handle providing the date from the url to the parent component
      callback(updatedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastProcessDateFromAPI]);

  function setProcessDate(date: string | null) {
    // if date is cleared the display date should reset to the last process date.
    if (!date) {
      setDisplayValue(formatUTCDate(lastProcessDateFromAPI));
    } else {
      setDisplayValue(date);
    }
    updateUrl(date);
    setInputValue(date || "");
  }

  return [InputValue, DisplayValue, setProcessDate];
}
